import { ReactNode } from 'react';
import Image from 'next/image';

import { Box, Heading, List, ListItem, Text } from '@chakra-ui/react';

import { ContentContainer } from '~/components/container';
import { Glyph } from '~/components/glyph';
import { StandardHeroSection } from '~/components/standard-hero';
import { useI18n } from '~/hooks/use-i18n';

export type HeroSectionProps = {
  title: string;
  blurb: string;
  features: Array<{
    glyphName: string;
    description: string;
  }>;
  buttonSlot: ReactNode;
};

export function HeroSection({ title, blurb, features, buttonSlot }: HeroSectionProps) {
  const { isRtl } = useI18n();
  return (
    <>
      <StandardHeroSection
        backgroundImageSlot={
          <Box inset={0} pointerEvents='none' position='absolute'>
            <Box
              inset={0}
              left={isRtl ? undefined : '50%'}
              position='absolute'
              right={isRtl ? '50%' : undefined}
              transform={isRtl ? 'translateX(50%)' : 'translateX(-50%)'}
              width={{
                base: '1440px',
              }}
            >
              <Image
                alt=''
                loading='eager'
                sizes='1440px'
                src='/images/ai/geometric-background-pattern.png'
                style={{
                  objectFit: 'cover',
                  objectPosition: 'center',
                }}
                fill
                priority
              />
            </Box>
          </Box>
        }
        containerProps={{
          bgGradient: 'linear(180deg, #6DEA79 0%, #05AA7E 100%)',
          minHeight: '400px',
          color: 'black',
          alignItems: 'center',
        }}
        contentSlot={
          <Box textAlign='center'>
            <Box mb={6}>
              <Heading
                as='h1'
                fontWeight='500 !important'
                mb={6}
                size={{
                  base: 'marketing-4xl',
                  md: 'marketing-5xl',
                }}
                whiteSpace={{
                  base: 'pre-wrap',
                  md: 'unset',
                }}
              >
                {title}
              </Heading>
              <Heading
                as='p'
                backgroundColor='black'
                color='white'
                display='inline'
                px={2}
                size={{
                  base: 'marketing-lg',
                  md: 'marketing-2xl',
                }}
              >
                {blurb}
              </Heading>
            </Box>
            {buttonSlot}
          </Box>
        }
      />
      <Box
        bgColor='accent.sea-foam.700'
        color='white'
        hidden={features.length === 0}
        padding={{
          base: '16px 0px',
          md: '32px 0px',
        }}
      >
        <ContentContainer>
          <List
            as='ul'
            display='flex'
            flexDir={{
              base: 'column',
              md: 'row',
            }}
            gap={{
              base: 4,
              md: '10px',
            }}
          >
            {features.map((feature, idx) => (
              <ListItem key={idx} alignItems='center' display='flex' flex='1' gap={4}>
                <Glyph height={48} name={feature.glyphName} width={48} />
                <Text>{feature.description}</Text>
              </ListItem>
            ))}
          </List>
        </ContentContainer>
      </Box>
    </>
  );
}
